import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Loader2,
  History,
  Copy,
  Check,
  FileText,
  Tag,
  Clock,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { generateUUID } from "@/utils/transform";
import { clauseApi } from "../../services/api";

// Loading skeleton
const ClauseEditSkeleton = () => (
  <div className="h-[calc(100vh-120px)] flex flex-col">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-24" />
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-32" />
      </div>
    </div>
    <div className="flex-1 flex gap-4">
      <Skeleton className="w-72 h-full" />
      <Skeleton className="flex-1 h-full" />
      <Skeleton className="w-80 h-full" />
    </div>
  </div>
);

export const ClauseEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");

  const [form, setForm] = useState({
    name: "",
    description: "",
    content: "",
    categoryId: "",
    tags: "",
    status: "draft",
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ["clause-categories"],
    queryFn: async () => {
      const res = await clauseApi.getCategories();
      const cats = res.data?.data;
      return Array.isArray(cats) ? cats : cats?.flat || [];
    },
  });

  // Fetch clause details (only when editing)
  const { data: clauseData, isLoading: clauseLoading } = useQuery({
    queryKey: ["clause", id],
    queryFn: async () => {
      const res = await clauseApi.getById(id);
      return res.data?.data;
    },
    enabled: !isNew && !!id,
  });

  // Fetch versions (only when editing and versions tab is active)
  const {
    data: versionsData,
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useQuery({
    queryKey: ["clause-versions", id],
    queryFn: async () => {
      const res = await clauseApi.getVersions(id);
      const versionsData = res.data?.data;
      return Array.isArray(versionsData) ? versionsData : [];
    },
    enabled: !isNew && !!id && activeTab === "versions",
  });

  // Populate form when clause data is loaded
  useEffect(() => {
    if (clauseData) {
      setForm({
        name: clauseData.name || "",
        description: clauseData.description || "",
        content: clauseData.content || "",
        categoryId: clauseData.categoryId || "",
        tags: clauseData.tags?.join(", ") || "",
        status: clauseData.status || "draft",
      });
    }
  }, [clauseData]);

  // Save mutation (create or update)
  const saveMutation = useMutation({
    mutationFn: (data) => {
      if (isNew) {
        return clauseApi.create(data);
      }
      return clauseApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clauses"] });
      if (isNew) {
        toast.success(t("clauses.created"));
        navigate("/clause-library");
      } else {
        toast.success(t("clauses.updated"));
        queryClient.invalidateQueries({ queryKey: ["clause", id] });
      }
    },
    onError: (error) => {
      console.error("Failed to save clause:", error);
      toast.error(error.response?.data?.error?.message || t("errors.generic"));
    },
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: () => clauseApi.approve(id),
    onSuccess: () => {
      setForm({ ...form, status: "approved" });
      toast.success(t("clauses.approved"));
      queryClient.invalidateQueries({ queryKey: ["clause", id] });
      queryClient.invalidateQueries({ queryKey: ["clauses"] });
    },
    onError: (error) => {
      console.error("Failed to approve clause:", error);
      toast.error(t("errors.generic"));
    },
  });

  // Create version mutation
  const createVersionMutation = useMutation({
    mutationFn: () => clauseApi.createVersion(id, { content: form.content }),
    onSuccess: () => {
      toast.success(t("common.versionCreated"));
      queryClient.invalidateQueries({ queryKey: ["clause-versions", id] });
    },
    onError: (error) => {
      console.error("Failed to create version:", error);
      toast.error(t("errors.generic"));
    },
  });

  const categories = categoriesData || [];
  const versions = versionsData || [];
  const loading = !isNew && clauseLoading;
  const saving =
    saveMutation.isPending ||
    approveMutation.isPending ||
    createVersionMutation.isPending;

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error(t("validation.nameRequired"));
      return;
    }
    if (!form.content.trim()) {
      toast.error(t("validation.contentRequired"));
      return;
    }

    const data = {
      ...form,
      categoryId: form.categoryId || null,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };

    saveMutation.mutate(data);
  };

  const handleApprove = () => {
    if (isNew) return;
    approveMutation.mutate();
  };

  const handleCreateVersion = () => {
    if (isNew) return;
    createVersionMutation.mutate();
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(form.content);
      setCopied(true);
      toast.success(t("common.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("errors.generic"));
    }
  };

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setShowVersionDialog(true);
  };

  const handleRestoreVersion = () => {
    if (selectedVersion) {
      setForm({ ...form, content: selectedVersion.content });
      setShowVersionDialog(false);
      toast.success(t("common.versionRestored"));
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === "versions" && !isNew && id) {
      refetchVersions();
    }
  };

  const renderVersionsContent = () =>
    versions.length === 0 ? (
      <div className="text-center text-muted-foreground py-8">
        <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{t("common.noVersionsYet")}</p>
        <p className="text-xs mt-1">{t("clauses.clickSaveVersion")}</p>
      </div>
    ) : (
      <div className="space-y-2">
        {versions.map((version, index) => (
          <button
            key={version.id || index}
            onClick={() => handleViewVersion(version)}
            className="w-full text-left p-3 rounded-lg border bg-background hover:bg-muted transition-colors"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-sm">
                Version {version.version || versions.length - index}
              </span>
              <Badge variant="outline" className="text-xs">
                {version.content?.length || 0} chars
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {version.createdAt
                ? new Date(version.createdAt).toLocaleDateString()
                : t("common.unknown")}
            </div>
            {version.createdByName && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                <User className="h-3 w-3" />
                {version.createdByName}
              </div>
            )}
          </button>
        ))}
      </div>
    );

  if (loading) {
    return <ClauseEditSkeleton />;
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/clause-library"
            className="flex items-center justify-center h-8 w-8 rounded-lg border hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-heading text-foreground">
              {isNew ? t("clauses.createClause") : t("clauses.editClause")}
            </h1>
            {!isNew && (
              <p className="text-sm text-muted-foreground">{form.name}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && form.status === "draft" && (
            <Button variant="outline" onClick={handleApprove} disabled={saving}>
              <Check className="w-4 h-4 mr-2" />
              Approve
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={handleCreateVersion}
              disabled={saving}
            >
              <History className="w-4 h-4 mr-2" />
              Save Version
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isNew ? t("common.create") : t("common.save")}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Left Panel - Clause Details */}
        <Card className="w-72 p-4 overflow-y-auto shrink-0">
          <h3 className="font-medium text-foreground mb-4">
            {t("clauses.details")}
          </h3>

          <div className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label className="text-foreground">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Standard NDA Clause"
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">
                {t("common.description")}
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder={t("placeholders.clauseDescription")}
                rows={2}
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">{t("common.category")}</Label>
              <Select
                value={form.categoryId || "none"}
                onValueChange={(value) =>
                  setForm({
                    ...form,
                    categoryId: value === "none" ? "" : value,
                  })
                }
              >
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder={t("placeholders.selectCategory")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("common.none")}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">{t("common.status")}</Label>
              <Select
                value={form.status}
                onValueChange={(value) => setForm({ ...form, status: value })}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("common.draft")}</SelectItem>
                  <SelectItem value="approved">
                    {t("common.approved")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">{t("common.tags")}</Label>
              <Input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="contract, nda, legal"
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Separate with commas
              </p>
            </div>
          </div>

          <hr className="my-4 border-border" />

          {/* Quick Info */}
          <h3 className="font-medium text-foreground mb-3">
            {t("common.quickInfo")}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>{form.content.length} characters</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Tag className="h-4 w-4" />
              <span>
                {form.tags.split(",").filter((t) => t.trim()).length} tags
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant={form.status === "approved" ? "default" : "secondary"}
              >
                {form.status}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Center Panel - Content Editor */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <Card className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-medium text-foreground">
                {t("clauses.content")}
              </h3>
              <Button variant="ghost" size="sm" onClick={handleCopyToClipboard}>
                {copied ? (
                  <Check className="h-4 w-4 mr-2 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                {copied ? t("common.copied") : t("common.copy")}
              </Button>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={t("placeholders.enterClauseTextFull")}
                className="h-full resize-none font-mono text-sm bg-background"
              />
            </div>
          </Card>
        </div>

        {/* Right Panel - Preview & Versions */}
        <Card className="w-80 shrink-0 overflow-hidden flex flex-col">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex-1 flex flex-col"
          >
            <div className="border-b px-4">
              <TabsList className="h-12">
                <TabsTrigger value="preview">{t("common.preview")}</TabsTrigger>
                <TabsTrigger value="versions">Versions</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="preview" className="flex-1 overflow-hidden m-0">
              <ScrollArea className="h-full">
                <div className="p-4">
                  <div className="bg-muted/50 rounded-lg p-4 min-h-50">
                    {form.content ? (
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <p className="whitespace-pre-wrap text-sm">
                          {form.content}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center text-muted-foreground py-8">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          {t("analytics.startTypingPreview")}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Tags Preview */}
                  {form.tags && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium mb-2">
                        {t("common.tags")}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {form.tags
                          .split(",")
                          .filter((t) => t.trim())
                          .map((tag) => (
                            <Badge
                              key={generateUUID()}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag.trim()}
                            </Badge>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent
              value="versions"
              className="flex-1 overflow-hidden m-0"
            >
              <ScrollArea className="h-full">
                <div className="p-4">
                  {versionsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    renderVersionsContent()
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Version Preview Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version {selectedVersion?.version || ""}</DialogTitle>
            <DialogDescription>
              {selectedVersion?.createdAt && (
                <>
                  Created on{" "}
                  {new Date(selectedVersion.createdAt).toLocaleString()}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-muted rounded-lg p-4 max-h-100 overflow-auto">
            <pre className="whitespace-pre-wrap text-sm font-mono">
              {selectedVersion?.content}
            </pre>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVersionDialog(false)}
            >
              Close
            </Button>
            <Button onClick={handleRestoreVersion}>
              {t("common.restoreVersion")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClauseEdit;
